import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ErrorAlert } from "@/components/ui/ErrorAlert";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { SettingsSection } from "@/components/SettingsSection";
import { SettingsRow } from "@/components/SettingsRow";
import { useDeleteAccountMutation } from "@/features/account/hooks/useAccount";
import { extractMessage } from "@/lib/errors";

export function DangerZoneSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deleteAccount = useDeleteAccountMutation();
  const [password, setPassword] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setConfirmOpen(true);
  }

  async function handleConfirm() {
    setConfirmOpen(false);
    setError(null);
    try {
      await deleteAccount.mutateAsync({ password });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(extractMessage(err));
    }
  }

  return (
    <SettingsSection title={t("account.danger.title")}>
      <SettingsRow
        label={t("account.danger.deleteLabel")}
        description={t("account.danger.deleteDescription")}
      >
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <Input
            type="password"
            placeholder={t("account.danger.passwordPlaceholder")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className="w-48"
          />
          <Button type="submit" variant="destructive" size="sm" disabled={deleteAccount.isPending}>
            {t("account.danger.deleteButton")}
          </Button>
        </form>
      </SettingsRow>
      {error && (
        <div className="px-4 py-3">
          <ErrorAlert message={error} />
        </div>
      )}
      <ConfirmDialog
        open={confirmOpen}
        title={t("account.danger.confirmTitle")}
        description={t("account.danger.confirmDescription")}
        confirmLabel={t("account.danger.deleteButton")}
        destructive
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </SettingsSection>
  );
}
